import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./index.scss";

import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

import { Text, Button } from "@fragments";
import { IconSchool, IconPhoto, IconArrowUpRight, IconUsers, IconClockHour4 } from "@tabler/icons-react";

import api from "@helpers/api";
import Parser from "@helpers/parser";

import { adaptProgram } from "@pages/CommunityConfig/tabs/Mentoring/helpers/apiAdapters";
import { getDurationUnitLabel } from "@pages/CommunityConfig/tabs/Mentoring/enums/durationUnits";
import { adaptMyEnrollmentListItem } from "@pages/Mentoring/helpers/apiAdapters";

// Statuses past the application review — the viewer is in the program.
const ACCEPTED_STATUSES = ["awaiting_community", "active", "split_only", "ended"];

/**
 * Community home — active mentoring programs strip, rendered above
 * "Sessões de conteúdo". Lists the community's programs
 * (GET community/{id}/mentoring/programs, ApiDog 35358032) and shows only the
 * active ones: a single program gets a full-width horizontal card (banner left,
 * info right); two or more go side by side in a carousel (same pattern as the
 * sections listing), each as a vertical card.
 */
// Target slide width: the card's 320px max + the item gap.
const SLIDE_WIDTH = 356;

export default function MentoringBar({ info }) {
    const { t } = useTranslation("community");

    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);

    // Slides per view derived from the real container width (the page content
    // area, not the viewport), so each slide stays ~the card width instead of
    // stretching to containerWidth / breakpoint-items.
    const wrapperRef = useRef(null);
    const [itemsPerView, setItemsPerView] = useState(3);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;

        const compute = () => {
            setItemsPerView(Math.max(1, Math.round(el.clientWidth / SLIDE_WIDTH)));
        };

        compute();
        const observer = new ResizeObserver(compute);
        observer.observe(el);
        return () => observer.disconnect();
    }, [loading, programs.length]);

    // program_id → the viewer's enrollment, to decide each card's CTA.
    const [enrollmentsByProgram, setEnrollmentsByProgram] = useState({});

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!info?.id) return;
            setLoading(true);

            // The api helper resolves errors (e.g. a 401 for guests) instead of
            // throwing — res.data.data is simply absent and the strip hides /
            // the enrollments map stays empty.
            const [programsRes, enrollmentsRes] = await Promise.all([
                api.get(`community/${info.id}/mentoring/programs?page=1&per_page=20&active=true`),
                api.get("mentoring/my-enrollments", { per_page: 50 }),
            ]);
            if (cancelled) return;

            const items = Array.isArray(programsRes?.data?.data) ? programsRes.data.data : [];
            setPrograms(items.map(adaptProgram).filter((p) => p?.active));

            const rows = Array.isArray(enrollmentsRes?.data?.data) ? enrollmentsRes.data.data : [];
            const byProgram = {};
            for (const row of rows.map(adaptMyEnrollmentListItem)) {
                if (!row.program?.id) continue;
                const current = byProgram[row.program.id];
                // Prefer the live enrollment when an old rejected/declined one
                // exists for the same program.
                if (!current || ["rejected", "declined"].includes(current.status)) {
                    byProgram[row.program.id] = row;
                }
            }
            setEnrollmentsByProgram(byProgram);

            setLoading(false);
        }

        load();
        return () => { cancelled = true; };
    }, [info?.id]);

    if (loading || programs.length === 0) return null;

    const single = programs.length === 1;

    return (
        <div className="mentoring-bar pt-1 pb-2">
            <div className="header py-1 csf-2 screen-padding">
                <IconSchool className="section-icon" />
                <h3 className="bold">{t("mentoringBar.title")}</h3>
            </div>

            {single ? (
                <div className="screen-padding mt-1">
                    <ProgramCard
                        program={programs[0]}
                        enrollment={enrollmentsByProgram[programs[0].id]}
                        membersCount={info?.members?.total}
                        horizontal
                    />
                </div>
            ) : (
                <div className="carousel-wrapper mt-1" ref={wrapperRef}>
                    <Carousel
                        key={`ipv-${itemsPerView}`}
                        responsive={{
                            all: {
                                breakpoint: { max: 10000, min: 0 },
                                items: itemsPerView,
                                slidesToSlide: Math.max(1, itemsPerView - 1),
                            },
                        }}
                        infinite={false}
                        draggable={true}
                        swipeable={true}
                        keyBoardControl={true}
                        containerClass="mentoring-bar-list"
                        itemClass="mentoring-carousel-item"
                        partialVisible={false}
                        centerMode={false}
                    >
                        {programs.map((program, index) => (
                            <div key={program.id ?? index}>
                                <ProgramCard
                                    program={program}
                                    enrollment={enrollmentsByProgram[program.id]}
                                    membersCount={info?.members?.total}
                                />
                            </div>
                        ))}
                    </Carousel>
                </div>
            )}
        </div>
    );
}

function ProgramCard({ program, enrollment, membersCount, horizontal = false }) {
    const { t } = useTranslation("community");
    const navigate = useNavigate();

    // Program reach when the viewer is a manager (owner metrics); the
    // community's member count for everyone else.
    const members = program.metrics?.total_members ?? membersCount ?? null;

    // The unit labels live in the mentoringPrograms namespace; the enum's keys
    // carry that prefix, so this component's `community`-bound `t` resolves them.
    const durationText = program.duration_value
        ? `${program.duration_value} ${getDurationUnitLabel(t)[program.duration_unit] ?? ""}`.trim()
        : t("mentoringBar.freeDuration");

    const accepted = enrollment && ACCEPTED_STATUSES.includes(enrollment.status);

    function handleApply() {
        // Already enrolled: the CTA opens the creator's mentoring page, where
        // the application result / program status lives.
        if (enrollment) {
            navigate(`/mentoring/${enrollment.id}`);
            return;
        }

        const application = program.application;

        if (application?.type === "external" && application.url) {
            window.open(application.url, "_blank", "noopener");
            return;
        }

        // form_id arrives hashed from the API (Slug::form()), ready for the URL.
        if (application?.type === "form" && application.form_id) {
            window.open(`https://forms.ginastee.com/${application.form_id}`, "_blank", "noopener");
            return;
        }

        window.setSnackbar?.(t("mentoringBar.applicationsClosed"));
    }

    const ctaText = !enrollment
        ? t("mentoringBar.apply")
        : accepted
            ? t("mentoringBar.viewMentoring")
            : t("mentoringBar.viewApplicationResult");

    return (
        <article className={`mtb-card${horizontal ? " is-horizontal" : ""}`}>
            <div className="mtb-banner">
                {program.banner?.url ? (
                    <img src={program.banner.url} alt={program.name} loading="lazy" />
                ) : (
                    <div className="mtb-banner-fallback">
                        <IconPhoto size={26} />
                    </div>
                )}
            </div>

            <div className="mtb-info">
                <Text.h2 contrast={9} weight={500} className="mtb-name">
                    {program.name}
                </Text.h2>

                {program.description && (
                    <Text.h5 contrast={6} maxLines={2} className="mtb-desc">
                        {program.description}
                    </Text.h5>
                )}

                <div className="mtb-metrics">
                    {members != null && (
                        <Text.span contrast={6} left={<IconUsers size={14} />}>
                            {t("mentoringBar.members", { value: Parser.number(members) })}
                        </Text.span>
                    )}
                    <Text.span contrast={6} left={<IconClockHour4 size={14} />}>
                        {durationText}
                    </Text.span>
                </div>

                <div className="mtb-cta">
                    <Button
                        text={ctaText}
                        color={enrollment && !accepted ? "overlay" : "inverse"}
                        endIcon={<IconArrowUpRight size={16} />}
                        highlightColor={enrollment && !accepted ? false : "current"}
                        onClick={handleApply}
                        preventLoading
                        fullWidth={!horizontal}
                    />
                </div>
            </div>
        </article>
    );
}
